import getSymbolFromCurrency from 'currency-symbol-map'

import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import {
  getCurrency,
  isSupportedCountry,
  useCurrencySymbol,
} from '@/lib/locale/country'
import {getCurrencyFromLocale, isSupportedLocale} from '@/lib/locale/locale'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import roundToNDecimal from '@/utils/numbers/roundToNDecimals'

import style from './ProductCard.module.scss'

interface Props {
  price: number | string
  countryOrLocale?: string | null | undefined
}

export default memo(function ProductPrice({price, countryOrLocale}: Props) {
  const currencySymbol = useCurrencySymbol()

  const derivedCurrencySymbol = useMemo(() => {
    try {
      if (typeof countryOrLocale === 'string') {
        if (isSupportedCountry(countryOrLocale)) {
          const currency = getCurrency(countryOrLocale)
          invariant(currency, 'Currency not found')
          const symbol = getSymbolFromCurrency(currency)
          invariant(symbol, 'Symbol not found')
          return symbol
        }

        if (isSupportedLocale(countryOrLocale)) {
          const currency = getCurrencyFromLocale(countryOrLocale)
          const symbol = getSymbolFromCurrency(currency)
          invariant(symbol, 'Symbol not found')
          return symbol
        }
      }
    } catch {
      // do nothing
    }
    return currencySymbol
  }, [countryOrLocale, currencySymbol])

  const roundedPrice = typeof price === 'string' ? false : roundToNDecimal(price)

  return (
    <div className={style.ProductPrice}>
      {roundedPrice ? (
        <>
          <VisuallyHidden>{`Price: ${derivedCurrencySymbol}${roundedPrice}`}</VisuallyHidden>
          <span className={style.CurrencySign} aria-hidden>
            {derivedCurrencySymbol}
          </span>
          <span aria-hidden>{roundedPrice.toFixed(USD_DISPLAY_DECIMALS)}</span>
        </>
      ) : (
        <>
          <VisuallyHidden>{`Price: ${price}`}</VisuallyHidden>
          <span aria-hidden>{price}</span>
        </>
      )}
    </div>
  )
})
